// Per-model thinking/effort rules for Prompt Hub's "ThinkingEnabled"/"ReasoningEffort" fields.
// Mirrors the LiveKit worker's BEDROCK_REASONING_MODEL_PROFILES (helper.py), which is the
// system of record: every combination these rules prevent is a hard 400 from Bedrock, and the
// worker clamps the same way if a stale row ever reaches it. Keep both in sync.
export const REASONING_EFFORTS = ["low", "medium", "high", "xhigh", "max"];
export const DEFAULT_REASONING_EFFORT = "low";

export const REASONING_EFFORT_LABELS = {
  low: "Low (fastest)",
  medium: "Medium",
  high: "High",
  xhigh: "Extra high",
  max: "Max",
};

export const LLM_REASONING_PROFILES = {
  "anthropic.claude-sonnet-5-5": {
    label: "Claude Sonnet 5.5",
    canDisableThinking: true,
    efforts: REASONING_EFFORTS,
  },
  "anthropic.claude-haiku-5-5": {
    label: "Claude Haiku 5.5",
    canDisableThinking: true,
    efforts: REASONING_EFFORTS,
  },
  // Grok's reasoning is always on; Bedrock rejects effort "none" and "max" for it.
  "xai.grok-4.7": {
    label: "Grok 4.7",
    canDisableThinking: false,
    efforts: ["low", "medium", "high", "xhigh"],
  },
};

// With thinking off, both Claude 5.5 models reject effort above "high".
const THINKING_OFF_MAX_EFFORT_INDEX = REASONING_EFFORTS.indexOf("high");

export const getReasoningProfile = (model = "") => {
  const candidate = String(model || "").trim().split("/").pop();
  const match = Object.keys(LLM_REASONING_PROFILES).find(
    (suffix) => candidate === suffix || candidate.endsWith(`.${suffix}`)
  );
  return match ? LLM_REASONING_PROFILES[match] : null;
};

export const allowedReasoningEfforts = (model, thinkingEnabled) => {
  const profile = getReasoningProfile(model);
  if (!profile) return [];
  if (profile.canDisableThinking && !thinkingEnabled) {
    return profile.efforts.filter(
      (effort) => REASONING_EFFORTS.indexOf(effort) <= THINKING_OFF_MAX_EFFORT_INDEX
    );
  }
  return profile.efforts;
};

// The values to show and save for a model: a model without reasoning controls saves the
// defaults, Grok always saves thinking on, and an effort the model can't take is clamped the
// same way the worker would ("xhigh"/"max" with Claude thinking off -> "high", anything else
// unsupported -> "low").
export const normalizeReasoningSelection = (model, thinkingEnabled, reasoningEffort) => {
  const profile = getReasoningProfile(model);
  if (!profile) {
    return { thinkingEnabled: false, reasoningEffort: DEFAULT_REASONING_EFFORT };
  }
  const thinking = profile.canDisableThinking ? Boolean(thinkingEnabled) : true;
  const requested = String(reasoningEffort || "").trim().toLowerCase();
  const allowed = allowedReasoningEfforts(model, thinking);
  let effort = DEFAULT_REASONING_EFFORT;
  if (allowed.includes(requested)) {
    effort = requested;
  } else if (profile.efforts.includes(requested) && allowed.includes("high")) {
    effort = "high";
  }
  return { thinkingEnabled: thinking, reasoningEffort: effort };
};

// Measured 2026-10-08 against Bedrock with a real ~3.5k-token practice prompt (median time to
// the first spoken word or tool call). Shown as guidance next to the controls.
export const reasoningLatencyHint = (model, thinkingEnabled, reasoningEffort) => {
  const profile = getReasoningProfile(model);
  if (!profile) return "";
  const effortIndex = REASONING_EFFORTS.indexOf(reasoningEffort);
  if (!profile.canDisableThinking && effortIndex >= REASONING_EFFORTS.indexOf("medium")) {
    return `${profile.label} took 5-9 seconds before its first word at Medium or higher in testing. Use Low for live calls.`;
  }
  if (thinkingEnabled && effortIndex >= REASONING_EFFORTS.indexOf("high")) {
    return `${profile.label} with thinking on took 1.3-3 seconds before its first word at High or above in testing. Low or Medium is recommended for live calls.`;
  }
  return "";
};
